import { FaroTable } from "@components/common/faro-table/faro-table";
import { FaroTableColumnVisibilityMenu } from "@components/common/faro-table/faro-table-column-visibility-menu";
import { TeamMemberBulkButtonComponents } from "@components/common/faro-table/faro-table-types";
import { useMembersColumns } from "@components/table/members/members-column/use-members-columns";
import { TEAM_MEMBERS_SORT_BY_MAP } from "@components/table/members/members-sort-constants";
import { MEMBERS_TABLE_COLUMN_LABELS } from "@components/table/members/members-table-constants";
import { getTeamMembersSortByFromGridSortModel } from "@components/table/members/members-table-utils";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { BaseMembersProps } from "@custom-types/member-types";
import { useHandleColumnOrderChange } from "@hooks/use-handle-column-order-change";
import { GridSortModel } from "@mui/x-data-grid-pro";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useAppDispatch } from "@store/store-helper";
import { setTableType } from "@store/table/table-slice";
import { API_MEMBERS_PER_REQUEST } from "@store/teams/teams-slice-utils";
import { TableType } from "@utils/track-event/track-event-list";
import { useCallback, useEffect } from "react";

interface Props extends BaseCompanyIdProps, BaseMembersProps {
  /** Whether the data for showing a table is loading or not */
  isLoading: boolean;

  /** The team to display members for */
  team: FaroCoreApiTypes.Team;

  /** Whether to hide the checkbox on each row or not */
  shouldHideCheckbox?: boolean;

  /** List of all the bulk actions */
  bulkActionButtons: TeamMemberBulkButtonComponents;

  /** Callback for loading more items (for pagination) */
  loadMoreItems: () => Promise<void>;

  /** Whether there are more items to load */
  hasMoreItems: boolean;

  /** Current sort by value from the sort dropdown */
  sortBy: FaroCoreApiTypes.TeamMemberSortBy;

  /** Total count of team members */
  teamMembersTotalCount: number;

  /** Callback to update the sort by value in the sort dropdown */
  setSortBy: (sortBy: FaroCoreApiTypes.TeamMemberSortBy) => void;
}

/**
 * A table component specifically for displaying team members with pagination
 */
export function TeamMembersTable({
  companyId,
  members,
  isLoading,
  team,
  bulkActionButtons,
  shouldHideCheckbox = false,
  loadMoreItems,
  hasMoreItems,
  sortBy,
  setSortBy,
  teamMembersTotalCount,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();

  // Make sure the correct table type is set in the store.
  useEffect(() => {
    dispatch(setTableType(TableType.teamMembers));
  }, [dispatch]);

  // Definition of the displayed columns
  const columns = useMembersColumns({
    companyId,
    tableSubject: "team",
    team,
  });

  const handleColumnOrderChange = useHandleColumnOrderChange(
    TableType.teamMembers
  );

  /**
   * Convert the sortBy prop to a GridSortModel for the table UI.
   */
  const sortModel = [TEAM_MEMBERS_SORT_BY_MAP[sortBy]];

  // Callback fired when the sort option changes
  const handleSortModelChange = useCallback(
    (newModel: GridSortModel) => {
      const newSortBy = getTeamMembersSortByFromGridSortModel(newModel);

      // Sync the sort dropdown with the table sort
      setSortBy(newSortBy);
    },
    [setSortBy]
  );

  return (
    <FaroTable
      entities={members}
      uniqueIdKey="identity"
      columns={columns}
      isLoading={isLoading}
      pageSize={API_MEMBERS_PER_REQUEST}
      hasMoreItems={hasMoreItems}
      totalCount={teamMembersTotalCount}
      onNextPage={loadMoreItems}
      bulkActionButtons={bulkActionButtons}
      shouldHideCheckbox={shouldHideCheckbox}
      sortModel={sortModel}
      onSortModelChange={handleSortModelChange}
      onColumnOrderChange={handleColumnOrderChange}
      tableType={TableType.teamMembers}
      paginationMode="server"
      sortTrackingSubject="Team Member"
      filterMenuTrigger={
        <FaroTableColumnVisibilityMenu
          tableType={TableType.teamMembers}
          labelsMap={MEMBERS_TABLE_COLUMN_LABELS}
          disabledColumns={["user"]}
        />
      }
      sx={{
        "& .MuiDataGrid-columnHeader:hover .drag-handle": {
          visibility: "visible",
          display: "flex",
        },
        /** Fill all the header */
        "& .MuiDataGrid-columnHeaderTitleContainerContent": {
          width: "100%",
        },
      }}
    />
  );
}
